import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Route, BookOpen, Trash2, Edit, Users, Clock,
  CheckCircle2, Search, UserPlus, Loader2, ToggleLeft, ToggleRight,
  Target, Monitor, Presentation, ChevronRight, ChevronLeft,
  ArrowRight, GripVertical, Eye, Zap, Star, MapPin, Calendar as CalIcon,
  BarChart3
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AssignmentModal from "../../components/shared/AssignmentModal";

/* ─── Visual Roadmap Component ─── */
const PathRoadmap = ({ pathCourses, allCourses }) => {
  if (!pathCourses || pathCourses.length === 0) return null;
  return (
    <div className="relative">
      <div className="flex items-center gap-0 overflow-x-auto py-3 px-1 pb-4 scrollbar-thin" data-testid="path-roadmap">
        {pathCourses.map((pc, i) => {
          const course = allCourses.find(c => c.id === pc.course_id);
          const isILT = course?.course_type === "ilt";
          return (
            <div key={pc.course_id} className="flex items-center shrink-0">
              <div className={`relative flex flex-col items-center group`}>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center border-2 transition-all ${
                  isILT
                    ? "bg-amber-50 border-amber-300 group-hover:border-amber-400 group-hover:shadow-amber-100"
                    : "bg-blue-50 border-blue-300 group-hover:border-blue-400 group-hover:shadow-blue-100"
                } group-hover:shadow-md`}>
                  {isILT ? <Presentation className="w-4.5 h-4.5 text-amber-600" /> : <Monitor className="w-4.5 h-4.5 text-blue-600" />}
                </div>
                <div className="mt-1.5 text-center w-[100px]">
                  <p className="text-[9px] font-semibold leading-tight truncate" title={course?.title || "Unknown"}>{course?.title || "Unknown"}</p>
                  <Badge variant="outline" className={`mt-0.5 text-[7px] px-1 py-0 h-3.5 ${isILT ? "text-amber-600 border-amber-200" : "text-blue-600 border-blue-200"}`}>
                    {isILT ? "ILT" : "eLearning"}
                  </Badge>
                </div>
                {!pc.is_required && (
                  <span
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center text-[8px] font-bold text-amber-600 cursor-help"
                    title="Optional course — not required for path completion"
                    data-testid={`optional-badge-${pc.course_id}`}
                  >?</span>
                )}
              </div>
              {i < pathCourses.length - 1 && (
                <div className="flex items-center mx-2 mt-[-18px]">
                  <div className="w-8 h-0.5 bg-slate-200 relative">
                    <ArrowRight className="w-3 h-3 text-slate-300 absolute -right-1.5 -top-[5px]" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {/* Spacer to prevent last item clipping */}
        <div className="shrink-0 w-4" />
      </div>
    </div>
  );
};

/* ─── Wizard Step Indicator ─── */
const WizardSteps = ({ currentStep, steps }) => (
  <div className="flex items-center gap-1 mb-5" data-testid="wizard-steps">
    {steps.map((step, i) => (
      <div key={i} className="flex items-center">
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
          i === currentStep ? "bg-primary text-white" :
          i < currentStep ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"
        }`}>
          {i < currentStep ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-4 text-center">{i + 1}</span>}
          <span className="hidden sm:inline">{step}</span>
        </div>
        {i < steps.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300 mx-0.5" />}
      </div>
    ))}
  </div>
);

const WIZARD_STEPS = ["Details", "eLearning", "ILT Sessions", "Review"];

const LearningPathsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = user?.role === "manager" ? "/manager" : user?.role === "tenant_admin" ? "/tenant" : "/admin";
  const [paths, setPaths] = useState([]);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [courseSearch, setCourseSearch] = useState("");
  const [searchFilter, setSearchFilter] = useState("");

  const [form, setForm] = useState({
    title: "", description: "", estimated_hours: 0, difficulty: "beginner", target_audience: "", courses: []
  });
  const [assignUserIds, setAssignUserIds] = useState([]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [pathsRes, coursesRes, usersRes] = await Promise.all([
        api.get("/learning-paths"), api.get("/courses"), api.get("/users").catch(() => ({ data: [] }))
      ]);
      setPaths(pathsRes.data || []);
      const c = coursesRes.data?.courses || coursesRes.data || [];
      setCourses(Array.isArray(c) ? c : []);
      const u = usersRes.data?.users || usersRes.data || [];
      setUsers(Array.isArray(u) ? u : []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const eLearningCourses = courses.filter(c => c.course_type !== "ilt");
  const iltCourses = courses.filter(c => c.course_type === "ilt");

  const handleSave = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        estimated_hours: form.estimated_hours,
        courses: form.courses.map((c, i) => ({ course_id: c.course_id, order: i + 1, is_required: c.is_required !== false }))
      };
      if (editMode && selectedPath) {
        await api.put(`/learning-paths/${selectedPath.id}`, payload);
        toast.success("Path updated");
      } else {
        await api.post("/learning-paths", payload);
        toast.success("Learning path created!");
      }
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this learning path?")) return;
    try { await api.delete(`/learning-paths/${id}`); toast.success("Deleted"); fetchData(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const handlePublishToggle = async (path) => {
    try {
      await api.put(`/learning-paths/${path.id}`, { is_published: !path.is_published });
      toast.success(path.is_published ? "Unpublished" : "Published");
      fetchData();
    } catch (e) { toast.error("Failed to update"); }
  };

  const handleAssign = async () => {
    if (!selectedPath || assignUserIds.length === 0) return;
    setSaving(true);
    try {
      await api.post(`/learning-paths/${selectedPath.id}/assign`, assignUserIds);
      toast.success(`Assigned to ${assignUserIds.length} users`);
      setShowAssign(false);
      setAssignUserIds([]);
    } catch (e) { toast.error("Failed to assign"); }
    finally { setSaving(false); }
  };

  const openEdit = (path) => {
    setEditMode(true);
    setSelectedPath(path);
    setForm({
      title: path.title, description: path.description || "",
      estimated_hours: path.estimated_hours || 0, difficulty: "beginner", target_audience: "",
      courses: path.courses || []
    });
    setWizardStep(0);
    setShowCreate(true);
  };

  const addCourseToPath = (courseId) => {
    if (form.courses.find(c => c.course_id === courseId)) return;
    const course = courses.find(c => c.id === courseId);
    setForm(prev => ({
      ...prev,
      courses: [...prev.courses, { course_id: courseId, order: prev.courses.length + 1, is_required: true }],
      estimated_hours: prev.estimated_hours + (course?.duration_hours || 0)
    }));
  };

  const removeCourseFromPath = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    setForm(prev => ({
      ...prev,
      courses: prev.courses.filter(c => c.course_id !== courseId),
      estimated_hours: Math.max(0, prev.estimated_hours - (course?.duration_hours || 0))
    }));
  };

  const moveCourse = (index, direction) => {
    const newCourses = [...form.courses];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newCourses.length) return;
    [newCourses[index], newCourses[newIndex]] = [newCourses[newIndex], newCourses[index]];
    setForm({ ...form, courses: newCourses });
  };

  const resetForm = () => {
    setForm({ title: "", description: "", estimated_hours: 0, difficulty: "beginner", target_audience: "", courses: [] });
    setEditMode(false); setSelectedPath(null); setWizardStep(0); setCourseSearch("");
  };

  const getCourseName = (courseId) => courses.find(c => c.id === courseId)?.title || courseId;
  const getCourseType = (courseId) => courses.find(c => c.id === courseId)?.course_type || "elearning";

  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  /* ─── Wizard Step Content ─── */
  const renderWizardStep = () => {
    switch (wizardStep) {
      case 0: // Details
        return (
          <div className="space-y-4">
            <div>
              <Label>Path Title *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Electrical Safety Fundamentals" className="mt-1" data-testid="path-title-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} placeholder="Describe the learning objectives and outcomes..." className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Difficulty Level</Label>
                <Select value={form.difficulty} onValueChange={v => setForm({...form, difficulty: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Target Audience</Label>
                <Input value={form.target_audience} onChange={e => setForm({...form, target_audience: e.target.value})} placeholder="e.g., New Hires, Electricians" className="mt-1" />
              </div>
            </div>
          </div>
        );

      case 1: // eLearning
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <Monitor className="w-4 h-4 text-blue-500" />
              <h3 className="text-sm font-semibold">Add eLearning / SCORM Courses</h3>
              <Badge className="bg-blue-50 text-blue-600 text-[10px]">{form.courses.filter(c => getCourseType(c.course_id) !== "ilt").length} added</Badge>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search eLearning courses..." value={courseSearch} onChange={e => setCourseSearch(e.target.value)} className="pl-9" data-testid="elearning-search" />
            </div>
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
              {eLearningCourses.filter(c => c.title?.toLowerCase().includes(courseSearch.toLowerCase())).slice(0, 30).map(c => {
                const isAdded = form.courses.some(fc => fc.course_id === c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => isAdded ? removeCourseFromPath(c.id) : addCourseToPath(c.id)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${
                      isAdded ? "border-blue-400 bg-blue-50/50 ring-1 ring-blue-200" : "hover:bg-muted/50"
                    }`}
                    data-testid={`elearning-${c.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isAdded ? "bg-blue-500" : "bg-blue-50"}`}>
                      {isAdded ? <CheckCircle2 className="w-4 h-4 text-white" /> : <Monitor className="w-4 h-4 text-blue-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.title}</p>
                      <p className="text-[10px] text-muted-foreground">{c.category || "General"} · {c.duration_hours || 0}h</p>
                    </div>
                    {isAdded && <Badge className="bg-blue-100 text-blue-700 text-[9px]">Added</Badge>}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 2: // ILT
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <Presentation className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-semibold">Add Instructor-Led Training (ILT)</h3>
              <Badge className="bg-amber-50 text-amber-600 text-[10px]">{form.courses.filter(c => getCourseType(c.course_id) === "ilt").length} added</Badge>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search ILT sessions..." value={courseSearch} onChange={e => setCourseSearch(e.target.value)} className="pl-9" data-testid="ilt-search" />
            </div>
            {iltCourses.length === 0 ? (
              <div className="text-center py-10 border rounded-lg border-dashed">
                <Presentation className="w-8 h-8 text-amber-300 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No ILT courses available</p>
                <p className="text-xs text-muted-foreground">ILT courses can be created from the Courses page</p>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
                {iltCourses.filter(c => c.title?.toLowerCase().includes(courseSearch.toLowerCase())).map(c => {
                  const isAdded = form.courses.some(fc => fc.course_id === c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => isAdded ? removeCourseFromPath(c.id) : addCourseToPath(c.id)}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${
                        isAdded ? "border-amber-400 bg-amber-50/50 ring-1 ring-amber-200" : "hover:bg-muted/50"
                      }`}
                      data-testid={`ilt-${c.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isAdded ? "bg-amber-500" : "bg-amber-50"}`}>
                        {isAdded ? <CheckCircle2 className="w-4 h-4 text-white" /> : <Presentation className="w-4 h-4 text-amber-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.title}</p>
                        <p className="text-[10px] text-muted-foreground">{c.category || "General"} · {c.duration_hours || 0}h · Instructor-Led</p>
                      </div>
                      {isAdded && <Badge className="bg-amber-100 text-amber-700 text-[9px]">Added</Badge>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 3: // Review
        return (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-muted/30 border space-y-2">
              <h3 className="font-bold text-lg">{form.title || "Untitled Path"}</h3>
              {form.description && <p className="text-sm text-muted-foreground">{form.description}</p>}
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {form.courses.length} courses</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {form.estimated_hours}h</span>
                <Badge variant="outline" className="text-[10px] capitalize">{form.difficulty}</Badge>
                {form.target_audience && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {form.target_audience}</span>}
              </div>
            </div>

            {/* Visual Roadmap Preview */}
            <div>
              <Label className="mb-2 block text-xs font-semibold text-muted-foreground uppercase tracking-wider">Path Roadmap</Label>
              <div className="border rounded-xl p-3 bg-white">
                <PathRoadmap pathCourses={form.courses} allCourses={courses} />
              </div>
            </div>

            {/* Course Sequence - Reorderable */}
            <div>
              <Label className="mb-2 block text-xs font-semibold text-muted-foreground uppercase tracking-wider">Course Sequence (drag to reorder)</Label>
              {form.courses.length === 0 ? (
                <p className="text-center py-6 text-muted-foreground text-sm">No courses added yet</p>
              ) : (
                <div className="space-y-1.5">
                  {form.courses.map((c, i) => {
                    const course = courses.find(co => co.id === c.course_id);
                    const isILT = course?.course_type === "ilt";
                    return (
                      <div key={c.course_id} className={`flex items-center gap-2 p-2.5 rounded-lg border ${isILT ? "border-amber-200 bg-amber-50/30" : "border-blue-200 bg-blue-50/30"}`}>
                        <GripVertical className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="text-[10px] font-mono text-muted-foreground w-5">{i + 1}.</span>
                        <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${isILT ? "bg-amber-100" : "bg-blue-100"}`}>
                          {isILT ? <Presentation className="w-3 h-3 text-amber-600" /> : <Monitor className="w-3 h-3 text-blue-600" />}
                        </div>
                        <span className="text-sm flex-1 truncate">{course?.title || c.course_id}</span>
                        <Badge variant="outline" className={`text-[8px] ${isILT ? "text-amber-600 border-amber-200" : "text-blue-600 border-blue-200"}`}>
                          {isILT ? "ILT" : "eLearning"}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">{course?.duration_hours || 0}h</span>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveCourse(i, -1)} disabled={i === 0}>
                            <ChevronLeft className="w-3 h-3 rotate-90" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveCourse(i, 1)} disabled={i === form.courses.length - 1}>
                            <ChevronRight className="w-3 h-3 rotate-90" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => removeCourseFromPath(c.course_id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <AdminLayout title="Learning Paths" variant={variant}>
      <div className="space-y-5" data-testid="learning-paths-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight" data-testid="paths-title">Learning Paths</h2>
            <p className="text-sm text-muted-foreground">{paths.length} paths configured</p>
          </div>
          <Button onClick={() => { resetForm(); setShowCreate(true); }} data-testid="create-path-btn">
            <Plus className="w-4 h-4 mr-1" /> New Path
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Paths", value: paths.length, icon: Route, color: "#3b82f6", bg: "bg-blue-50" },
            { label: "Published", value: paths.filter(p => p.is_published).length, icon: CheckCircle2, color: "#22c55e", bg: "bg-green-50" },
            { label: "Draft", value: paths.filter(p => !p.is_published).length, icon: Edit, color: "#f59e0b", bg: "bg-amber-50" },
            { label: "Total Courses", value: paths.reduce((s, p) => s + (p.courses?.length || 0), 0), icon: BookOpen, color: "#8b5cf6", bg: "bg-violet-50" },
          ].map((st, i) => (
            <Card key={i} className="border shadow-none">
              <CardContent className="p-3.5 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg ${st.bg} flex items-center justify-center`}>
                  <st.icon className="w-4 h-4" style={{ color: st.color }} />
                </div>
                <div>
                  <p className="text-xl font-bold leading-none">{st.value}</p>
                  <p className="text-[10px] text-muted-foreground">{st.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search paths..." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} className="pl-9 max-w-xs" data-testid="search-paths" />
        </div>

        {/* Paths List */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : paths.filter(p => p.title?.toLowerCase().includes(searchFilter.toLowerCase())).length === 0 ? (
          <Card className="border shadow-none">
            <CardContent className="py-16 text-center">
              <Route className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <h3 className="font-semibold mb-1">{paths.length === 0 ? "No learning paths yet" : "No matching paths"}</h3>
              <p className="text-sm text-muted-foreground mb-4">Create a learning path to sequence courses into a structured journey</p>
              {paths.length === 0 && <Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4 mr-1" /> Create Path</Button>}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {paths.filter(p => p.title?.toLowerCase().includes(searchFilter.toLowerCase())).map((path) => (
              <motion.div key={path.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="border shadow-none hover:shadow-sm transition-shadow cursor-pointer" data-testid={`path-card-${path.id}`}
                  onClick={() => navigate(`${basePath}/learning-paths/${path.id}`)}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shrink-0">
                        <Route className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="font-semibold text-sm truncate hover:text-primary transition-colors">{path.title}</h3>
                          <Badge variant={path.is_published ? "default" : "outline"} className="text-[10px]">
                            {path.is_published ? "Published" : "Draft"}
                          </Badge>
                        </div>
                        {path.description && <p className="text-xs text-muted-foreground mb-2 line-clamp-1">{path.description}</p>}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                          <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{path.courses?.length || 0} courses</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{path.estimated_hours}h</span>
                          <span className="flex items-center gap-1">
                            <Monitor className="w-3 h-3 text-blue-500" />
                            {path.courses?.filter(c => getCourseType(c.course_id) !== "ilt").length || 0} eLearning
                          </span>
                          <span className="flex items-center gap-1">
                            <Presentation className="w-3 h-3 text-amber-500" />
                            {path.courses?.filter(c => getCourseType(c.course_id) === "ilt").length || 0} ILT
                          </span>
                        </div>

                        {/* Visual Roadmap */}
                        <PathRoadmap pathCourses={path.courses} allCourses={courses} />
                      </div>

                      <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => navigate(`${basePath}/learning-paths/${path.id}`)} data-testid={`analytics-path-${path.id}`}>
                          <BarChart3 className="w-3.5 h-3.5 mr-1" /> Analytics
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setSelectedPath(path); setAssignUserIds([]); setShowAssign(true); }} data-testid={`assign-path-${path.id}`}>
                          <UserPlus className="w-3.5 h-3.5 mr-1" /> Assign
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handlePublishToggle(path)}>
                          {path.is_published ? <ToggleRight className="w-4 h-4 text-green-500" /> : <ToggleLeft className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(path)}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" onClick={() => handleDelete(path.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Create/Edit Wizard Dialog ─── */}
      <Dialog open={showCreate} onOpenChange={v => { if (!v) resetForm(); setShowCreate(v); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit" : "Create"} Learning Path</DialogTitle>
            <DialogDescription>Build a structured learning journey for your team</DialogDescription>
          </DialogHeader>

          <WizardSteps currentStep={wizardStep} steps={WIZARD_STEPS} />

          <div className="flex-1 overflow-y-auto min-h-0 pr-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={wizardStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.15 }}
              >
                {renderWizardStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          <DialogFooter className="border-t pt-3 flex items-center justify-between">
            <div className="flex-1 text-xs text-muted-foreground">
              {form.courses.length > 0 && `${form.courses.length} courses · ${form.estimated_hours}h total`}
            </div>
            <div className="flex items-center gap-2">
              {wizardStep > 0 && (
                <Button variant="outline" onClick={() => { setWizardStep(wizardStep - 1); setCourseSearch(""); }}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              {wizardStep < WIZARD_STEPS.length - 1 ? (
                <Button onClick={() => {
                  if (wizardStep === 0 && !form.title) { toast.error("Title is required"); return; }
                  setWizardStep(wizardStep + 1); setCourseSearch("");
                }} data-testid="wizard-next">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSave} disabled={saving} data-testid="save-path-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  {editMode ? "Update" : "Create"} Path
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Assign Modal ─── */}
      <AssignmentModal
        open={showAssign}
        onOpenChange={setShowAssign}
        title={selectedPath?.title || ""}
        onAssign={async (userIds) => {
          if (!selectedPath) return;
          try {
            await api.post(`/learning-paths/${selectedPath.id}/assign`, userIds);
            toast.success(`Assigned to ${userIds.length} users`);
          } catch (e) { toast.error("Failed to assign"); }
        }}
      />
    </AdminLayout>
  );
};

export default LearningPathsPage;
